import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, CopyIcon, DownloadIcon, EyeIcon, Loader2Icon, MailIcon, SendIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { QuoteLinesEditor } from '../../components/admin/QuoteLinesEditor';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate, formatDateTime, telHref } from '../../utils/format';
import { QuoteEmailDialog } from '../../components/admin/QuoteEmailDialog';
import { QuotePdfPreview } from '../../components/admin/QuotePdfPreview';
import { downloadQuotePdf } from '../../utils/quotePdf';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteLine, QuoteStatus } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
const SENT_WARNING = 'Dette tilbud er allerede sendt. Ændringer kan betyde, at kunden har en tidligere version.';
export function QuoteDetail() {
  const {
    quoteId
  } = useParams();
  const navigate = useNavigate();
  const {
    quotes,
    cases,
    updateQuote,
    setQuoteStatus,
    duplicateQuote,
    deleteQuote,
    createCaseFromQuote,
    authLoading,
    lastWriteError,
    settings
  } = useAdminData();
  const quote = quotes.find(entry => entry.id === quoteId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  /** Kundeoplysninger og PDF-tekster — alt kan rettes før tilbuddet sendes. */
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [intro, setIntro] = useState('');
  const [terms, setTerms] = useState('');
  const [closing, setClosing] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [pdfPreview, setPdfPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!quote) return;
    setTitle(quote.title);
    setDescription(quote.description);
    setLines(quote.lines);
    setQuoteDate(quote.quoteDate);
    setValidUntil(quote.validUntil);
    setInternalNotes(quote.internalNotes);
    setCustomerNotes(quote.customerNotes);
    setCustomerName(quote.customerName);
    setCustomerEmail(quote.customerEmail);
    setCustomerPhone(quote.customerPhone);
    setCustomerAddress(quote.customerAddress);
    setIntro(quote.intro);
    setTerms(quote.terms);
    setClosing(quote.closing);
    setPaymentTerms(quote.paymentTerms);
  }, [quote?.id]);
  if (!quote) return <Navigate to="/admin/tilbud" replace data-id="element-1540" data-mp="39esrz2pl7zk" />;
  const status = effectiveStatus(quote);
  const linkedCase = cases.find(item => item.id === quote.caseId || item.quoteId === quote.id);
  const locked = quote.status === 'Accepteret';
  const history = [...(quote.history ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  async function handleSave() {
    if (saving) return;
    // Der skrives aldrig, mens Firebase stadig gendanner sessionen.
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    if (locked) {
      const confirmed = window.confirm('Tilbuddet er accepteret af kunden. Er du sikker på, at du vil ændre pris og indhold?');
      if (!confirmed) return;
    }
    // Et allerede sendt tilbud må aldrig ændres i stilhed.
    if (quote.status === 'Sendt' && !window.confirm(`${SENT_WARNING}\n\nVil du gemme alligevel?`)) {
      return;
    }
    setSaving(true);
    setError('');
    const result = await updateQuote(quote.id, {
      title,
      description,
      lines,
      quoteDate,
      validUntil,
      internalNotes,
      customerNotes,
      customerName,
      customerEmail,
      customerPhone,
      customerAddress,
      intro,
      terms,
      closing,
      paymentTerms
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1541" data-mp="2xr8mki812t6m">
      <Link to="/admin/tilbud" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-1542" data-mp="2xoe06l5geyak">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1543" data-mp="37gs4nlnx2j0f" />
        Tilbud
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-1544" data-mp="2jr7mh4zl7ps7">
        <div data-id="element-1545" data-mp="2eso0j4kzudzu">
          <p className="text-sm text-ink-muted" data-id="element-1546" data-mp="2eswaanltl36t">{quote.quoteNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1547" data-mp="2tp5abrdjbkka">
            {quote.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-1548" data-mp="2os50r6hh7zof">
            {quote.customerName} · {formatKr(quote.total)} inkl. moms
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-1549" data-mp="3yrwa5eg8bma">
          <QuoteStatusBadge status={status} data-id="element-1550" data-mp="b39kezim69oe" />
          <SelectField id="tilbud-status" label="Status" className="w-52" value={quote.status} onChange={async event => {
          setWorking('status');
          setError('');
          const result = await setQuoteStatus(quote.id, event.target.value as QuoteStatus);
          setWorking('');
          if (!result.ok) {
            setError(result.error ?? 'Statussen kunne ikke gemmes. Prøv igen.');
          }
        }} options={quoteStatuses.filter(option => option !== 'Udløbet').map(option => ({
          value: option,
          label: option
        }))} data-id="element-1551" data-mp="b2hy6fawpoas" />
        </div>
      </div>

      {sentTo && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-1552" data-mp="35ren96n1somd">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1553" data-mp="3aoeql9sijd9s">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1554" data-mp="1ntjopv2qdxmc" />
          </span>
          Tilbuddet er sendt til {sentTo}
        </p>}

      {quote.status === 'Sendt' && <p className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1555" data-mp="1iwjjfmdpgywa">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1556" data-mp="1dxq73xa5ndxp" />
          {SENT_WARNING}
        </p>}

      {status === 'Udløbet' && <p className="mt-5 flex items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-1557" data-mp="143zjeulur7j2">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1558" data-mp="101uta5um4t16" />
          Tilbuddet er udløbet ({formatDate(quote.validUntil)}). Forlæng gyldigheden, eller lav en kopi.
        </p>}

      {/* Sag kan oprettes uanset tilbuddets status — status ændres ikke. */}
        <section className="mt-5 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-1559" data-mp="l8rh6dzsj7hu">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-1560" data-mp="2a1nebbn00mr4">
            <div data-id="element-1561" data-mp="2e164altofo0t">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1562" data-mp="2e1o4tundgoej">
                {linkedCase ? 'Sag allerede oprettet' : 'Sag'}
              </h2>
              <p className="mt-1 text-[15px] text-ink-soft" data-id="element-1563" data-mp="250q2rhwhuqyl">
                {linkedCase ? `Sag #${linkedCase.number} — ${linkedCase.title}` : quote.status === 'Accepteret' ? 'Tilbuddet er accepteret. Opret sagen, så arbejdet kan planlægges.' : 'Du kan oprette sagen nu — tilbuddets status forbliver ' + status.toLowerCase() + '.'}
              </p>
            </div>
            {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className={buttonClasses('secondary', 'md')} data-id="element-1564" data-mp="17dim6vb9hdg0">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-1565" data-mp="1cdn2olc66868" />
                Åbn sag
              </Link> : <Button size="md" disabled={working === 'case'} onClick={async () => {
          setWorking('case');
          setError('');
          const created = await createCaseFromQuote(quote);
          setWorking('');
          if (!created) {
            setError('Sagen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/sager/${created.id}`);
        }} data-id="element-1566" data-mp="srtz279uongd">
                {working === 'case' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1567" data-mp="1lf7o7j7b4epx" />
                    Opretter …
                  </> : 'Opret sag fra tilbud'}
              </Button>}
          </div>
        </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-1568" data-mp="1qgdfrdp8dvxv">
        <div className="space-y-5" data-id="element-1569" data-mp="63usncsfhlkk">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1570" data-mp="64w3ph9g282a">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1571" data-mp="1bjr65i1hrpw">
              Kunde på tilbuddet
            </h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1572" data-mp="3sj5pdilzp1zd">
              Oplysningerne her vises på PDF’en og kan rettes uden at ændre kundekartoteket.
            </p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1573" data-mp="p14d8x9hp53e">
              <TextField id="td-kundenavn" label="Navn" value={customerName} onChange={event => {
              setCustomerName(event.target.value);
              setSaved(false);
            }} data-id="element-1574" data-mp="k32qo2cuunn6" />
              <TextField id="td-kundeadresse" label="Adresse" value={customerAddress} onChange={event => {
              setCustomerAddress(event.target.value);
              setSaved(false);
            }} data-id="element-1575" data-mp="b4qrvd1u4jjn" />
              <TextField id="td-kundemail" label="E-mail" type="email" value={customerEmail} onChange={event => {
              setCustomerEmail(event.target.value);
              setSaved(false);
            }} data-id="element-1576" data-mp="1b8rmqto12e5b" />
              <TextField id="td-kundetlf" label="Telefon" type="tel" value={customerPhone} onChange={event => {
              setCustomerPhone(event.target.value);
              setSaved(false);
            }} data-id="element-1577" data-mp="1u1c9dtd4vn0o" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1578" data-mp="28028tfnfdys7">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1579" data-mp="9mk4408jecmf">Opgave</h2>
            {locked && <p className="mt-3 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1580" data-mp="dn3zozdgp86g">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1581" data-mp="1gzktkvselx75" />
                Tilbuddet er accepteret. Ændringer i pris og indhold skal bekræftes, før de gemmes.
              </p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1582" data-mp="1lx2t5vkj702h">
              <TextField id="td-titel" label="Tilbudstitel" className="sm:col-span-2" value={title} onChange={event => {
              setTitle(event.target.value);
              setSaved(false);
            }} data-id="element-1583" data-mp="1lvjfmcq9f34e" />
              <TextField id="td-dato" label="Tilbudsdato" type="date" value={quoteDate} onChange={event => {
              setQuoteDate(event.target.value);
              setSaved(false);
            }} data-id="element-1584" data-mp="29o8cikpokahj" />
              <TextField id="td-gyldig" label="Gyldig til" type="date" value={validUntil} onChange={event => {
              setValidUntil(event.target.value);
              setSaved(false);
            }} data-id="element-1585" data-mp="2xa23xa0znir4" />
            </div>
            <TextArea id="td-beskrivelse" label="Beskrivelse af arbejdet" className="mt-5" rows={4} value={description} onChange={event => {
            setDescription(event.target.value);
            setSaved(false);
          }} data-id="element-1586" data-mp="2s9o76o4u7v8e" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1587" data-mp="29k7tt08208ak">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1588" data-mp="29i56inyact9j">Tilbudslinjer</h2>
            <div className="mt-5" data-id="element-1589" data-mp="1vmjhigs1y9h4">
              <QuoteLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-1590" data-mp="20k1jfn7njofm" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1591" data-mp="1nkalcst41sp9">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1592" data-mp="1irz8yqh6oeft">Noter</h2>
            <TextArea id="td-kundenoter" label="Bemærkninger til kunden" className="mt-5" rows={3} value={customerNotes} onChange={event => {
            setCustomerNotes(event.target.value);
            setSaved(false);
          }} data-id="element-1593" data-mp="1es8lowqv8gg1" />
            <TextArea id="td-internenoter" label="Interne noter" hint="Vises aldrig på kundens PDF." className="mt-5" rows={3} value={internalNotes} onChange={event => {
            setInternalNotes(event.target.value);
            setSaved(false);
          }} data-id="element-1594" data-mp="zw948vhv93y8" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1595" data-mp="bwqktln0i9ks">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1596" data-mp="gwd1k521u7yc">Tekst på tilbud</h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1597" data-mp="lpfx2iqdzrh6">
              Teksterne herunder står på PDF’en og kan skrives individuelt for hvert tilbud.
            </p>
            <TextArea id="td-indledning" label="Indledning" className="mt-5" rows={3} value={intro} onChange={event => {
            setIntro(event.target.value);
            setSaved(false);
          }} data-id="element-1598" data-mp="3p64qd30yl4ow" />
            <TextArea id="td-betingelser" label="Betingelser" className="mt-5" rows={3} value={terms} onChange={event => {
            setTerms(event.target.value);
            setSaved(false);
          }} data-id="element-1599" data-mp="70jehrqpew2b" />
            <TextField id="td-betaling" label="Betalingsbetingelser" className="mt-5" value={paymentTerms} onChange={event => {
            setPaymentTerms(event.target.value);
            setSaved(false);
          }} data-id="element-1600" data-mp="3eg8igjn0eje1" />
            <TextArea id="td-afslutning" label="Afsluttende tekst" className="mt-5" rows={2} value={closing} onChange={event => {
            setClosing(event.target.value);
            setSaved(false);
          }} data-id="element-1601" data-mp="3jf1zun5f2ujh" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1602" data-mp="30p2bk2hehjh4">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1603" data-mp="2czriz8a7eve2" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-1604" data-mp="27zvb2xydyjt8">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-1605" data-mp="nj5f29wvlafd">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1606" data-mp="sbyimuglycpv" />
                  Gemmer …
                </> : 'Gem ændringer'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPdfPreview(true)} data-id="element-1607" data-mp="iihplsoap1n9">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-1608" data-mp="168c0w5syui5e" />
              Forhåndsvis PDF
            </Button>
            <Button variant="secondary" size="lg" disabled={downloading} onClick={async () => {
            setDownloading(true);
            setError('');
            try {
              await downloadQuotePdf(quote, settings);
            } catch {
              setError('PDF’en kunne ikke dannes. Prøv igen.');
            }
            setDownloading(false);
          }} data-id="element-1609" data-mp="1b75erzc60b63">
              {downloading ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1610" data-mp="dhmsewhgp2vz" />
                  Danner PDF …
                </> : <>
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1611" data-mp="3jyp3i5sin27" />
                  Download PDF
                </>}
            </Button>
            <Button variant="dark" size="lg" onClick={() => setComposing(true)} data-id="element-1612" data-mp="1177fnrknemb2">
              <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-1613" data-mp="w9p4duj5ruzw" />
              Send tilbud på mail
            </Button>
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1614" data-mp="rfkup3iid8v4">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1615" data-mp="mgqg6drkq6gt">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1616" data-mp="1f7rz8pye0m5m" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-1617" data-mp="1mkr3n9bfsysg">
            <Button variant="secondary" size="md" disabled={working === 'accept' || quote.status === 'Accepteret'} onClick={async () => {
            setWorking('accept');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Accepteret');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-1618" data-mp="1riiwq6ntiqy8">
              Marker som accepteret
            </Button>
            <Button variant="secondary" size="md" disabled={working === 'reject' || quote.status === 'Afvist'} onClick={async () => {
            setWorking('reject');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Afvist');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-1619" data-mp="2f9oeirdbjtb6">
              Marker som afvist
            </Button>
            <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-line px-4 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-1620" data-mp="6fqwwu52sjil">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1621" data-mp="kdxawpqidorc" />
              Slet tilbud
            </button>
          </div>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1622" data-mp="u5l8l8dns219">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1623" data-mp="u4s1g6asfail">Historik</h2>
            <ol className="mt-4 space-y-3" data-id="element-1624" data-mp="y8zolz1lkiep">
              {history.map(event => <li key={event.id} className="flex gap-3 text-[15px]" data-id="element-1625" data-mp="136ssglffh0bt">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" data-id="element-1626" data-mp="2nok3tq4zlid7" />
                  <span data-id="element-1627" data-mp="2enm0zb6abgw7">
                    <span className="text-ink" data-id="element-1628" data-mp="2emu31tp7t72r">{event.text}</span>
                    <span className="block text-sm text-ink-muted" data-id="element-1629" data-mp="2epgisgm9esw2">
                      {formatDateTime(event.at)}
                      {event.by ? ` · ${event.by}` : ''}
                    </span>
                  </span>
                </li>)}
              {history.length === 0 && <li className="text-sm text-ink-muted" data-id="element-1630" data-mp="2xmht8gyl1l7e">Ingen aktivitet registreret endnu.</li>}
            </ol>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-1631" data-mp="2smveu470pyx0">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1632" data-mp="3q2sgsavc8hab">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1633" data-mp="3q4vhbou49sta">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-1634" data-mp="3l9zc1mzvd8y8">
              {quote.customerName || '—'}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-1635" data-mp="d2jcr5ndjy2s">
              <li data-id="element-1636" data-mp="3vfz9qbenl95x">{quote.customerPhone || '—'}</li>
              <li className="break-all" data-id="element-1637" data-mp="3vf7pfwqiaxuy">{quote.customerEmail || '—'}</li>
              <li data-id="element-1638" data-mp="3boppz2b0uga">{quote.customerAddress || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-1639" data-mp="39u40ij7kyb5">
              {quote.customerPhone && <LinkButton href={telHref(quote.customerPhone)} variant="secondary" size="sm" data-id="element-1640" data-mp="3qgmmvqns823c">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1641" data-mp="3ch4vr4n0kcqt" />
                  Ring
                </LinkButton>}
              {quote.customerEmail && <LinkButton href={`mailto:${quote.customerEmail}`} variant="secondary" size="sm" data-id="element-1642" data-mp="3gkt3pofk5acl">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1643" data-mp="1annlrubeuzsw" />
                  Send e-mail
                </LinkButton>}
            </div>
            {quote.customerId && <Link to={`/admin/kunder/${quote.customerId}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-1644" data-mp="2z7bdw5vap95q">
                Se kundeprofil
              </Link>}
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-1645" data-mp="346nzyoecec79">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1646" data-mp="3ivk5oesxfd4p">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1647" data-mp="3rxtduy0q2uex">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1648" data-mp="3n09zly40yhmz">
                Åbn sag
              </Link>
            </section>}

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1649" data-mp="1snbs1m9plz3c">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1650" data-mp="1uzcmmmb5whup">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-1651" data-mp="1yzee62n6nuw9">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1652" data-mp="241c5zl9nfy0t">
                <dt className="text-ink-soft" data-id="element-1653" data-mp="28z59u7nhcfxx">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-1654" data-mp="28ylzpd42mvug">{formatKr(quote.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1655" data-mp="1b9k1bl2v66c0">
                <dt className="text-ink-soft" data-id="element-1656" data-mp="1gbrgskmwk4j3">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-1657" data-mp="1l9t3rgng8nzu">{formatKr(quote.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1658" data-mp="1q0j9iuzsesv2">
                <dt className="font-medium text-ink" data-id="element-1659" data-mp="1q1ubcp9o9qjj">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-1660" data-mp="sk400vjcu9uj">{formatKr(quote.total)}</dd>
              </div>
            </dl>
            {quote.lastSentAt ? <p className="mt-4 text-sm text-ink-muted" data-id="element-1661" data-mp="q0fqdexuenj9">
                Sendt til {quote.sentTo} den {formatDateTime(quote.lastSentAt)}
              </p> : <p className="mt-4 text-sm text-ink-muted" data-id="element-1662" data-mp="kzjpla8sfhi2">Tilbuddet er endnu ikke sendt til kunden.</p>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1663" data-mp="b3yg6r1e3eya">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1664" data-mp="71v01p3rzm6o">Handlinger</h2>
            <div className="mt-4 flex flex-col gap-2" data-id="element-1665" data-mp="2b4tw9nizfax">
              <Button variant="secondary" size="sm" disabled={working === 'copy'} onClick={async () => {
              setWorking('copy');
              setError('');
              const copy = await duplicateQuote(quote);
              setWorking('');
              if (!copy) {
                setError(lastWriteError() || 'Tilbuddet kunne ikke duplikeres. Prøv igen.');
                return;
              }
              navigate(`/admin/tilbud/${copy.id}`);
            }} data-id="element-1666" data-mp="29k40xc5okje">
                <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-1667" data-mp="39x7g10punk2c" />
                Dupliker tilbud
              </Button>
            </div>
          </section>
        </aside>
      </div>

      {pdfPreview && <QuotePdfPreview quote={quote} onClose={() => setPdfPreview(false)} data-id="element-1668" data-mp="30vhpdx3skp5m" />}

      {composing && <QuoteEmailDialog quote={quote} onClose={() => setComposing(false)} onSent={email => {
      setComposing(false);
      setSentTo(email);
    }} data-id="element-1669" data-mp="3ol28cfzy3mew" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-tilbud-titel" data-id="element-1670" data-mp="3jtkai0t7y1y5">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-1671" data-mp="g58phui55yuo">
            <h2 id="slet-tilbud-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-1672" data-mp="lhp32os2wayq">
              Er du sikker på, at du vil slette dette tilbud?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-1673" data-mp="uasllbhkze08">
              {quote.quoteNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1674" data-mp="2jl2wju65m92">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1675" data-mp="gjvujp3az4va" />
                Tilbuddet er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men mister
                henvisningen til tilbuddet.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-1676" data-mp="1mxv6v1x8xs8j">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-1677" data-mp="3hgpx4dsxzh0d">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteQuote(quote.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Tilbuddet kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/tilbud');
          }} data-id="element-1678" data-mp="3lhrxixylhz3x">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1679" data-mp="hxx9oiwmkrt3" />
                    Sletter …
                  </> : 'Slet tilbud'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-1680" data-mp="2c7lno5z4mthl">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}